import { z } from 'zod';
import { PURCHASE_TYPES, REJECTION_SECTIONS } from './domain.js';
/** One request line (SOW §4.3). Prices use string decimals to avoid float drift. */
export const requestLineSchema = z.object({
    itemDescription: z.string().min(1, 'Item description is required').max(500),
    // Optional for the requester; a reviewer must fill it in before approving (`missingForApproval`).
    procurementCategoryId: z.string().optional(),
    quantity: z
        .string()
        .regex(/^\d+(\.\d{1,4})?$/, 'Quantity must be a decimal with up to 4 places')
        .refine((value) => Number(value) > 0, 'Quantity must be greater than 0'),
    unitOfMeasure: z.string().min(1, 'Unit of measure is required'),
    netPrice: z
        .string()
        .regex(/^\d+(\.\d{1,4})?$/, 'Unit price must be a decimal with up to 4 places'),
    deliveryDate: z.iso.date('Delivery date is required'),
    // Optional, never block the requester from moving on. The sales tax group is a code from
    // D365's live list (never persisted here); the dates are the period the line covers.
    startDate: z.iso.date().optional(),
    endDate: z.iso.date().optional(),
    salesTaxGroupId: z.string().optional(),
    vendorItemDescription: z.string().max(500).optional(),
    vendorPartNo: z.string().max(100).optional(),
});
/** Request header (SOW §4.2). Company code drives dependent master data. */
export const requestHeaderSchema = z.object({
    purchaseType: z.enum(PURCHASE_TYPES),
    projectId: z.string().optional(), // mandatory when purchaseType === CAPITAL (refined below)
    // Optional for the requester, who often does not know it; a reviewer must supply it before
    // approving a Capital request that has a project (`missingForApproval`).
    projectCategoryId: z.string().optional(),
    companyCode: z.string().min(1, 'Company is required'),
    // Optional for the requester; required at approval (`missingForApproval`).
    // The D365 main account is the first segment of the ledger account and differs per request
    // (expense vs capital, and per category), so it is chosen each time, from D365's own list.
    mainAccountId: z.string().optional(),
    costCentreId: z.string().optional(),
    lineOfBusinessId: z.string().optional(),
    warehouseId: z.string().min(1, 'Warehouse is required'),
    vendorAccount: z.string().min(1, 'Vendor is required'),
    paymentTerms: z
        .string()
        .min(1, 'This vendor has no payment terms set in D365 — ask your D365 administrator to add one before submitting'),
    currencyCode: z
        .string()
        .length(3, 'This vendor has no currency set in D365 — ask your D365 administrator to add one before submitting'),
    termStartDate: z.iso.date().optional(),
    termEndDate: z.iso.date().optional(),
    deliveryDate: z.iso.date('Delivery date is required'),
    internalNotes: z.string().max(2000).optional(),
    notesForVendor: z.string().max(2000).optional(),
});
/** `01 Aug 2026` — parsed from the ISO date parts directly so it can never drift a
 * day from timezone conversion, and formatted to match the wireframe's own example. */
const MESSAGE_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
function formatIsoDateForMessage(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return `${String(day).padStart(2, '0')} ${MESSAGE_MONTHS[month - 1]} ${year}`;
}
export const submitRequestSchema = requestHeaderSchema
    .extend({ lines: z.array(requestLineSchema).min(1, 'Add at least one line item') })
    .superRefine((r, ctx) => {
    if (r.purchaseType === 'CAPITAL' && !r.projectId) {
        ctx.addIssue({
            code: 'custom',
            message: 'Project ID is mandatory for Capital purchases',
            path: ['projectId'],
        });
    }
    // Per line, not in `requestLineSchema` itself: that schema is also `.partial()`-ed for
    // drafts, which a refinement on it would forbid.
    for (const [index, line] of r.lines.entries()) {
        if (line.startDate && line.endDate && line.endDate < line.startDate) {
            ctx.addIssue({
                code: 'custom',
                message: `Cannot be before start date (${formatIsoDateForMessage(line.startDate)})`,
                path: ['lines', index, 'endDate'],
            });
        }
    }
    if (r.termStartDate && r.termEndDate && r.termEndDate < r.termStartDate) {
        ctx.addIssue({
            code: 'custom',
            message: `Cannot be before term start date (${formatIsoDateForMessage(r.termStartDate)})`,
            path: ['termEndDate'],
        });
    }
});
/**
 * Drafts may be arbitrarily incomplete — everything optional. `id` is absent
 * on the first auto-save (which creates the request) and present on every
 * save after, so subsequent calls update the same row instead of creating a
 * new one each time.
 */
export const draftRequestSchema = requestHeaderSchema.partial().extend({
    id: z.uuid().optional(),
    lines: z.array(requestLineSchema.partial()).optional(),
    // `paymentTerms`/`currencyCode` are read-only, autofilled straight from the
    // selected vendor's D365 record (RequestForm.tsx) — never typed by the
    // user. `.partial()` only allows a field to be *absent*; it does not
    // relax `min(1)`/`length(3)` on a value that IS present, and D365 does
    // not guarantee either is populated (confirmed live: some vendors carry
    // an empty payment-terms string). Selecting such a vendor made every
    // subsequent autosave fail schema validation — surfaced to the requester
    // as nothing more than a permanently retrying "Could not save your
    // draft," since the validation detail never crosses the API boundary
    // (see errorHandler.ts). `submitRequestSchema` extends the strict,
    // non-partial `requestHeaderSchema` directly, so both are still fully
    // enforced — with a real, visible message — at actual submission.
    paymentTerms: z.string().optional(),
    currencyCode: z.string().length(3).optional().or(z.literal('')),
});
export const reviewDecisionSchema = z.discriminatedUnion('decision', [
    z.object({ decision: z.literal('APPROVE'), comment: z.string().max(2000).optional() }),
    z.object({
        decision: z.literal('REJECT'),
        comment: z.string().min(1).max(2000), // comment mandatory on rejection
        // #155 — which part of the request the rejection is about (wireframe V3 /
        // 08.3 rule 7), optional so an existing REJECT caller stays valid.
        section: z.enum(REJECTION_SECTIONS).optional(),
    }),
]);
/**
 * The values a requester may leave empty but a reviewer must supply
 * before approving. One implementation shared by the API's approve gate and
 * the review screen's warning, so the two can never disagree about what is
 * missing. Empty string counts as missing — it is what the form sends for
 * "not chosen".
 */
export function missingForApproval(request, options = {}) {
    const items = [];
    if (options.mainAccountRequired !== false && !request.mainAccountId) {
        items.push({
            field: 'mainAccountId',
            label: 'Main account',
            message: 'Main account is required before approval',
        });
    }
    if (!request.costCentreId) {
        items.push({
            field: 'costCentreId',
            label: 'Cost centre',
            message: 'Cost centre is required before approval',
        });
    }
    if (!request.lineOfBusinessId) {
        items.push({
            field: 'lineOfBusinessId',
            label: 'Line of business',
            message: 'Line of business is required before approval',
        });
    }
    if (request.purchaseType === 'CAPITAL' && request.projectId && !request.projectCategoryId) {
        items.push({
            field: 'projectCategoryId',
            label: 'Project category',
            message: 'Project category is required before approval',
        });
    }
    for (const [index, line] of request.lines.entries()) {
        if (!line.procurementCategoryId) {
            const lineNo = index + 1;
            items.push({
                field: 'procurementCategoryId',
                lineNo,
                label: `Line ${String(lineNo)} procurement category`,
                message: `Line ${String(lineNo)}: procurement category is required before approval`,
            });
        }
    }
    return items;
}
